import { useId, type ReactNode } from "react";

import type { VehiclePreset } from "../types/traffic";

function vehicleMarkup(shadowId: string): Record<VehiclePreset, ReactNode> {
  const shadow = `url(#${shadowId})`;
  return {
    sedan: (
      <>
        <ellipse
          cx="90"
          cy="216"
          rx="38"
          ry="9"
          fill="#000"
          opacity=".34"
          filter={shadow}
        />
        <path
          d="M80 14 66 24 58 50 56 78 48 94 46 148 58 164 60 200 74 216h32l14-16 2-36 12-16-2-54-8-16-2-28-14-22z"
          fill="currentColor"
        />
        <rect x="64" y="20" width="12" height="8" rx="1.5" fill="#f8fbff" />
        <rect x="104" y="20" width="12" height="8" rx="1.5" fill="#f8fbff" />
        <path d="m60 80 16-30h28l16 30-12 8H72z" fill="#15191d" />
        <path d="M58 96h64v36H58z" fill="#252a30" />
        <path d="m62 134 8-6h40l8 6-3 12H65z" fill="#3a4248" />
        <path
          d="M48 124h84"
          stroke="#ece6d4"
          strokeOpacity=".65"
          strokeWidth="4"
        />
        <rect x="52" y="200" width="16" height="8" rx="1.5" fill="#ff3b4e" />
        <rect x="112" y="200" width="16" height="8" rx="1.5" fill="#ff3b4e" />
        <rect x="76" y="208" width="28" height="8" rx="1.5" fill="#ece6d4" />
        <path
          d="M118 92V66"
          stroke="#171a1e"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      </>
    ),
    hatchback: (
      <>
        <ellipse
          cx="90"
          cy="200"
          rx="50"
          ry="11"
          fill="#000"
          opacity=".34"
          filter={shadow}
        />
        <path
          d="M90 48C66 48 52 68 46 94L42 122 46 154C50 176 66 196 90 202C114 196 130 176 134 154L138 122 134 94C128 68 114 48 90 48Z"
          fill="currentColor"
        />
        <rect x="60" y="54" width="14" height="11" rx="6" fill="#f8fbff" />
        <rect x="106" y="54" width="14" height="11" rx="6" fill="#f8fbff" />
        <path d="m58 88 18-24h28l18 24-14 8H72z" fill="#15191d" />
        <path d="M54 104h72v28H54z" fill="#252a30" />
        <path d="M52 136h76l-8 28-12 10H72l-12-10z" fill="#3a4248" />
        <path
          d="M50 168h80"
          stroke="#171a1e"
          strokeWidth="6"
          strokeLinecap="round"
        />
        <rect x="50" y="176" width="22" height="12" rx="6" fill="#ff3b4e" />
        <rect x="108" y="176" width="22" height="12" rx="6" fill="#ff3b4e" />
        <rect x="76" y="190" width="28" height="7" rx="1.5" fill="#ece6d4" />
      </>
    ),
    "compact-suv": (
      <>
        <ellipse
          cx="90"
          cy="220"
          rx="62"
          ry="12"
          fill="#000"
          opacity=".38"
          filter={shadow}
        />
        <path
          d="M60 12 40 24 30 50 26 78 26 178 36 206 52 222h76l16-16 10-28V78l-4-28-10-26-20-12z"
          fill="currentColor"
        />
        <path d="M34 172h112l-6 28-12 16H52l-12-16z" fill="#111519" />
        <rect x="40" y="20" width="18" height="12" rx="2" fill="#f8fbff" />
        <rect x="122" y="20" width="18" height="12" rx="2" fill="#f8fbff" />
        <path d="m44 64 20-24h52l20 24-8 10H52z" fill="#15191d" />
        <path d="M40 86h100v52H40z" fill="#252a30" />
        <path d="M38 140h104l-6 16-10 8H54l-10-8z" fill="#3a4248" />
        <rect x="52" y="78" width="10" height="78" rx="3" fill="#ece6d4" />
        <rect x="118" y="78" width="10" height="78" rx="3" fill="#ece6d4" />
        <path
          d="M26 100h12v56H26zm116 0h12v56h-12z"
          fill="#111519"
          opacity=".75"
        />
        <rect x="36" y="194" width="26" height="12" rx="2" fill="#ff3b4e" />
        <rect x="118" y="194" width="26" height="12" rx="2" fill="#ff3b4e" />
        <rect x="68" y="210" width="44" height="8" rx="1.5" fill="#ece6d4" />
      </>
    ),
    minivan: (
      <>
        <ellipse
          cx="90"
          cy="222"
          rx="52"
          ry="11"
          fill="#000"
          opacity=".38"
          filter={shadow}
        />
        <path
          d="M74 6 52 20 42 50 38 76 38 192 50 216 68 230h44l18-14 12-24V76l-4-26-10-30-20-14z"
          fill="currentColor"
        />
        <rect x="48" y="16" width="18" height="9" rx="2" fill="#f8fbff" />
        <rect x="114" y="16" width="18" height="9" rx="2" fill="#f8fbff" />
        <path d="m50 56 16-26h48l16 26v14H50z" fill="#15191d" />
        <path d="M48 78h84v88l-14-8H62l-14 8z" fill="#252a30" />
        <path d="M72 78v80M108 78v80" stroke="#ece6d4" strokeWidth="5" />
        <path d="M42 98h7v68h-7zm89 0h7v68h-7z" fill="#ece6d4" />
        <rect x="122" y="128" width="10" height="22" rx="3" fill="#ece6d4" />
        <path d="M46 170h88l-4 20-12 10H62l-12-10z" fill="#3a4248" />
        <rect x="44" y="200" width="22" height="10" rx="2" fill="#ff3b4e" />
        <rect x="114" y="200" width="22" height="10" rx="2" fill="#ff3b4e" />
        <rect x="72" y="214" width="36" height="8" rx="1.5" fill="#ece6d4" />
      </>
    ),
    "retro-coupe": (
      <>
        <ellipse
          cx="90"
          cy="209"
          rx="55"
          ry="11"
          fill="#000"
          opacity=".36"
          filter={shadow}
        />
        <path
          d="M90 24 58 38 42 72 38 158l14 36 24 18h28l24-18 14-36-4-86-16-34z"
          fill="currentColor"
        />
        <path d="m54 76 24-30h24l24 30-12 13H66z" fill="#15191d" />
        <path d="M50 104h80v42H50z" fill="#282e34" />
        <path d="M44 158h92l-12 32H56z" fill="#14181c" />
        <circle cx="55" cy="54" r="8" fill="#fff6cf" />
        <circle cx="125" cy="54" r="8" fill="#fff6cf" />
        <rect x="48" y="182" width="24" height="10" rx="2" fill="#ff304f" />
        <rect x="108" y="182" width="24" height="10" rx="2" fill="#ff304f" />
        <rect x="74" y="199" width="32" height="7" rx="2" fill="#ece6d4" />
      </>
    ),
    "sports-coupe": (
      <>
        <ellipse
          cx="90"
          cy="204"
          rx="58"
          ry="10"
          fill="#000"
          opacity=".38"
          filter={shadow}
        />
        <path
          d="m90 20-34 22-18 54 4 72 22 34h52l22-34 4-72-18-54z"
          fill="currentColor"
        />
        <path d="m54 92 22-50h28l22 50-17 10H71z" fill="#101419" />
        <path d="m50 118 80 0-12 46-28 18-28-18z" fill="#242b31" />
        <path
          d="M48 57 64 43M132 57l-16-14"
          stroke="#f8fbff"
          strokeWidth="7"
          strokeLinecap="round"
        />
        <path d="M43 164h94" stroke="#14181c" strokeWidth="8" />
        <rect x="48" y="178" width="27" height="8" rx="4" fill="#ff304f" />
        <rect x="105" y="178" width="27" height="8" rx="4" fill="#ff304f" />
        <rect x="75" y="193" width="30" height="7" rx="2" fill="#ece6d4" />
      </>
    ),
    microcar: (
      <>
        <ellipse
          cx="90"
          cy="187"
          rx="43"
          ry="10"
          fill="#000"
          opacity=".34"
          filter={shadow}
        />
        <path
          d="M90 48 61 62 48 92v58l17 31 25 10 25-10 17-31V92l-13-30z"
          fill="currentColor"
        />
        <path d="m60 97 14-31h32l14 31-12 10H72z" fill="#15191d" />
        <rect x="58" y="116" width="64" height="31" rx="10" fill="#2a3036" />
        <circle cx="62" cy="78" r="7" fill="#f8fbff" />
        <circle cx="118" cy="78" r="7" fill="#f8fbff" />
        <rect x="57" y="158" width="19" height="9" rx="4" fill="#ff304f" />
        <rect x="104" y="158" width="19" height="9" rx="4" fill="#ff304f" />
        <rect x="76" y="177" width="28" height="7" rx="2" fill="#ece6d4" />
      </>
    ),
    "utility-wagon": (
      <>
        <ellipse
          cx="90"
          cy="221"
          rx="61"
          ry="12"
          fill="#000"
          opacity=".38"
          filter={shadow}
        />
        <path
          d="M58 10 38 28 30 66v119l18 26 20 13h44l20-13 18-26V66l-8-38-20-18z"
          fill="currentColor"
        />
        <path d="m44 66 20-38h52l20 38-10 10H54z" fill="#15191d" />
        <path d="M40 88h100v65H40z" fill="#252a30" />
        <path d="M90 88v65" stroke="#ece6d4" strokeWidth="4" opacity=".65" />
        <path d="M36 164h108l-9 34-17 15H62l-17-15z" fill="#171b20" />
        <rect x="39" y="199" width="28" height="10" rx="2" fill="#ff304f" />
        <rect x="113" y="199" width="28" height="10" rx="2" fill="#ff304f" />
        <rect x="69" y="214" width="42" height="7" rx="2" fill="#ece6d4" />
      </>
    ),
    roadster: (
      <>
        <ellipse
          cx="90"
          cy="200"
          rx="57"
          ry="10"
          fill="#000"
          opacity=".37"
          filter={shadow}
        />
        <path
          d="M90 30 54 49 38 91l6 77 21 30h50l21-30 6-77-16-42z"
          fill="currentColor"
        />
        <path d="m56 95 20-43h28l20 43-15 9H71z" fill="#0d1115" />
        <path d="M55 113h70l-8 45-27 17-27-17z" fill="#30373d" />
        <path
          d="M58 51 45 70M122 51l13 19"
          stroke="#f8fbff"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <rect x="49" y="172" width="25" height="9" rx="4" fill="#ff304f" />
        <rect x="106" y="172" width="25" height="9" rx="4" fill="#ff304f" />
        <rect x="74" y="190" width="32" height="7" rx="2" fill="#ece6d4" />
      </>
    ),
    "boxy-electric": (
      <>
        <ellipse
          cx="90"
          cy="222"
          rx="63"
          ry="12"
          fill="#000"
          opacity=".39"
          filter={shadow}
        />
        <path
          d="M48 8 30 35 26 190l21 31h86l21-31-4-155-18-27z"
          fill="currentColor"
        />
        <path d="M40 54 61 24h58l21 30-5 28H45z" fill="#15191d" />
        <path d="M38 95h104v65H38z" fill="#242a30" />
        <path d="M49 170h82l12 24-17 20H54l-17-20z" fill="#13171b" />
        <path
          d="M38 42h104"
          stroke="#f8fbff"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <rect x="38" y="193" width="31" height="9" rx="2" fill="#ff304f" />
        <rect x="111" y="193" width="31" height="9" rx="2" fill="#ff304f" />
        <rect x="69" y="213" width="42" height="7" rx="2" fill="#ece6d4" />
      </>
    ),
  };
}

type VehicleGraphicProps = {
  preset: VehiclePreset;
};

export function VehicleGraphic({ preset }: VehicleGraphicProps) {
  const shadowId = useId().replace(/:/g, "");
  return (
    <>
      <defs>
        <filter id={shadowId} x="-30%" y="-30%" width="160%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>
      {vehicleMarkup(shadowId)[preset]}
    </>
  );
}
